'use client'

import { useEffect, useRef, useState } from 'react'

/** 스크롤 방향 판정 임계값(px) — 미세 흔들림 무시 */
const SCROLL_THRESHOLD_PX = 8
/** 최하단 판정 여유(px) */
const BOTTOM_EPSILON_PX = 2

/**
 * 모바일 하단 고정 CTA 노출 제어 + 높이 측정 훅 (Figma 8624:168307 인터랙션).
 * - 진입 시 노출
 * - 스크롤 내리면 숨김 / 올리면 노출
 * - 최하단까지 내리면 노출
 *
 * `height`는 콘텐츠 하단 여백(spacer)이 CTA 높이만큼 자리를 예약해
 * 마지막 콘텐츠(공유 카드)가 CTA에 가리지 않게 하는 데 쓴다.
 */
export const useRankBottomCta = () => {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(true)
  const [height, setHeight] = useState(0)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const measure = () => setHeight(el.offsetHeight)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)

    let lastY = window.scrollY
    const onScroll = () => {
      const y = Math.max(0, window.scrollY)
      const atBottom =
        window.innerHeight + y >=
        document.documentElement.scrollHeight - BOTTOM_EPSILON_PX

      if (atBottom) {
        setVisible(true)
        lastY = y
        return
      }

      const delta = y - lastY
      // 임계값 미만은 무시하고 lastY를 유지해 느린 스크롤도 누적되게 한다.
      if (Math.abs(delta) < SCROLL_THRESHOLD_PX) return

      setVisible(delta < 0)
      lastY = y
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  return { ref, visible, height }
}
